/**
 * IMPORTS
 */

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import {
  IMinhasTarefasResponse,
  IRemessa,
} from "@/hooks/remessas/historico-de-remessa-do-cliente/index.d";

import { useStoreZustandHistoricoRemessa } from "@/store-zustand/cliente/historico-de-remessa-do-cliente";
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { handleGetHistoricoRemessa } from "@/domain/use-cases/remessas/historico-de-remessa-do-cliente";
import { generatePagination } from "@/utils/generate-pagination";
import { getStatusLabel } from "@/utils/get-status-label";

function mapHistoricoRemessaToTable(data: IRemessa[]): Record<string, any>[] {
  return data?.map((remessa) => ({
    remessa: remessa.id,
    numero_remessa: remessa.numero_remessa ?? "N/A",
    nome_identificacao: remessa.cliente?.nome ?? "N/A",
    quantidadeSolicitacao: remessa.total_solicitacoes ?? 0,
    status: getStatusLabel(remessa.situacao) ?? "N/A",
    data: remessa.created_at ? new Date(remessa.created_at).toLocaleString("pt-BR") : "N/A",
    data_inicio_producao: remessa.data_inicio_producao
      ? new Date(remessa.data_inicio_producao).toLocaleString("pt-BR")
      : "N/A",
    data_fim_producao: remessa.data_fim_producao
      ? new Date(remessa.data_fim_producao).toLocaleString("pt-BR")
      : "N/A",
    prazo: remessa.data_inicio_producao
      ? new Date(remessa.data_inicio_producao).toLocaleString("pt-BR")
      : "N/A",
    posicao: remessa.posicao ?? "N/A",
    tecnologia: remessa.tecnologia?.nome ?? "N/A",
    modelo: remessa.modelo_tecnico ?? "N/A",
    nome_modelo: remessa.modelo_tecnico?.nome_modelo ?? "N/A",
    solicitante: remessa.solicitante?.nome ?? "N/A",
    designer: remessa?.designer?.nome ?? "N/A",
    observacao: remessa?.observacao ?? "",
    hasMensagem: Boolean(remessa?.observacao !== null),
    historico_status_remessa: remessa?.historico_status_remessa,
    items_remessa: remessa?.items_remessa ?? [],
  }));
}

function useHistoricoRemessas() {
  const { currentPage, itemsPerPage, searchItem, setPaginations, setLastPage } =
    useStoreZustandHistoricoRemessa();

  const { token: accessToken, user } = useStoreZustandUserAuth();

  // Chave de cache única baseada na página atual e na quantidade de itens por página
  const queryKey = ["historico-remessas", currentPage, itemsPerPage, searchItem];

  const {
    isLoading,
    isFetching,
    error: isError,
    data: historicoRemessasResponse,
  } = useQuery<IMinhasTarefasResponse>({
    queryKey,
    queryFn: async () => {
      const idParams = user?.tipo_login === "subordinado" ? user?.cliente_principal?.id : user?.id;

      const historicoRemessasResponse = await handleGetHistoricoRemessa(
        accessToken!,
        idParams!,
        currentPage,
        itemsPerPage,
        searchItem!
      );

      return historicoRemessasResponse;
    },
    //diz ao React Query: "enquanto os novos dados estão carregando, mantenha os dados antigos"
    placeholderData: (previousData) => previousData,
  });

  const remessas = mapHistoricoRemessaToTable(historicoRemessasResponse?.data as IRemessa[]);

  const pagination = historicoRemessasResponse?.pagination;

  // só recalcula se currentPage ou last_page mudar
  const pages = useMemo(() => {
    return generatePagination(currentPage, pagination?.last_page ?? 1);
  }, [currentPage, pagination?.last_page]);

  //  atualiza zustand uma única vez
  useEffect(() => {
    setPaginations(pages as number[]);
    setLastPage(pagination?.last_page!);
  }, [pages, setPaginations, setLastPage, pagination]);

  return {
    historicoRemessas: remessas,
    paginations: pages,
    isLoading,
    historicoRemessasCarregando: isLoading || isFetching,
    isError,
  };
}

/**
 * EXPORTS
 */
export { useHistoricoRemessas };
